/* eslint-disable react-refresh/only-export-components */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useMediaQuery } from '@mui/material';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Pure mapping from a drag-end (active/over ids) to the from/to indices a reorder
 * needs. Returns null when nothing should move (item didn't move, or an id is unknown).
 */
export function getReorderIndices(
  ids: string[],
  activeId: string,
  overId: string
): { from: number; to: number } | null {
  if (activeId === overId) return null;
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from === -1 || to === -1) return null;
  return { from, to };
}

interface SortableListProps {
  /** Stable unique ids, in current order. */
  ids: string[];
  /** Called with array indices after a successful drag. */
  onReorder: (fromIndex: number, toIndex: number) => void;
  children: ReactNode;
  /** Renders the floating drag preview. Portaled by DragOverlay outside the Drawer. */
  renderOverlay?: (id: string) => ReactNode;
}

/**
 * Generic drag-to-reorder wrapper. Deliberately favorites-agnostic — it knows only ids
 * and an onReorder callback — so it can be reused for other sortable sidebar sections.
 *
 * Uses `DragOverlay` specifically so drag coordinates stay correct inside the sidebar
 * Drawer's transform/stacking context, and a `KeyboardSensor` so reordering is operable
 * without a pointer.
 */
export function SortableList({ ids, onReorder, children, renderOverlay }: SortableListProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const indices = getReorderIndices(ids, String(active.id), String(over.id));
    if (indices) onReorder(indices.from, indices.to);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
      <DragOverlay>{activeId && renderOverlay ? renderOverlay(activeId) : null}</DragOverlay>
    </DndContext>
  );
}

type UseSortableReturn = ReturnType<typeof useSortable>;

export interface SortableItemRenderProps {
  setNodeRef: (node: HTMLElement | null) => void;
  style: CSSProperties;
  attributes: UseSortableReturn['attributes'];
  listeners: UseSortableReturn['listeners'];
  isDragging: boolean;
}

interface SortableItemProps {
  id: string;
  children: (props: SortableItemRenderProps) => ReactNode;
}

/**
 * Render-prop wrapper around `useSortable`. Hands the row its node ref + transform style,
 * and the drag `attributes`/`listeners` to spread onto a dedicated handle (so the row body
 * stays clickable for navigation while only the handle initiates a drag).
 */
export function SortableItem({ id, children }: SortableItemProps) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: prefersReducedMotion ? 'none' : transition,
    opacity: isDragging ? 0.4 : undefined,
  };
  return <>{children({ setNodeRef, style, attributes, listeners, isDragging })}</>;
}
